import React from 'react';
import { Box } from '@mui/material';
import {
  CheckCircle as ValidationIcon,
} from '@mui/icons-material';
import MainLayout from '../../../shared/ui/layout/MainLayout';
import WorkspaceFooter from '../../../shared/ui/workflow/WorkspaceFooter';
import WorkspaceStatusBanner from '../../../shared/ui/workflow/WorkspaceStatusBanner';
import WorkspaceSpotlightTour from '../../../shared/ui/common/WorkspaceSpotlightTour';
import { PcfValidationContent, ValidationNavigationNotice } from '../../pcf-validation';
import { OperationProgressWindowRequest } from '../../../shared/ui/progress';
import { ExportPcfContent } from '../../export-pcf';
import { QuickFillPage as QuickFillPageRedux } from '../../quick-fill';
import MappingReadinessAlert from './MappingReadinessAlert';
import MappingProjectProfileGateDialog from './MappingProjectProfileGateDialog';
import MappingSetupSummaryDialog from './MappingSetupSummaryDialog';
import MappingWorkspaceGuideDialog from './MappingWorkspaceGuideDialog';
import MappingWorkspaceCanvas from './MappingWorkspaceCanvas';
import MappingWorkspaceLoadingPlaceholder from './components/MappingWorkspaceLoadingPlaceholder';
import ProfileSaveAsDialog from './components/ProfileSaveAsDialog';
import { MappingTabPanel } from '../../../shared/ui/mapping';
import { WorkspaceCallout } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { CaptionText } from '../../../shared/ui/typography';
import {
  getE2eBootstrapConfig,
  type E2eMappingProfileProgressState,
} from '../../../shared/config/e2eBootstrap';
import type { OperationProgressModel } from '../../../shared/ui/progress';
import { buildProfileApplyWindowModel } from '../lib/mappingProfileApplyProgressWindowModel';
import { stabilizeProfileApplyWindowModel } from '../lib/profile-apply-progress/modelStabilizer';
import { toMappingWorkspaceTabIndex } from '../lib/mappingWorkspacePageModel';
import { getProfileKeyParts } from '../lib/mappingPageProjectProfileUtils';
import { toTraceKey, traceMappingFlow } from '../lib/mappingTrace';
import { useMappingWorkspaceContentModel } from '../hooks/useMappingWorkspaceContentModel';
import { useIncludeDefaultPipeexParametersPrompt } from '../hooks/useIncludeDefaultPipeexParametersPrompt';
import type { MappingConfigurationPageContentProps } from '../hooks/mappingWorkspaceContentModel.types';

export type { MappingConfigurationPageContentProps };

function buildE2eProfileApplyProgressModel(
  state: E2eMappingProfileProgressState
): OperationProgressModel | null {
  const failed = state === 'failed';
  const rawModel = buildProfileApplyWindowModel({
    operationId: 'mapping-profile-apply:e2e',
    profileApplyProgress: {
      profileKey: 'default',
      profileName: 'Default 4',
      settingsType: 1,
      phase: 'complete',
      message: 'Default 4 profile loaded',
      startedAt: 1,
    },
    readinessProgressModel: {
      title: 'Creating missing Revit parameters',
      message: failed ? 'Could not create MATERIAL' : 'Creating BOLT_DIAMETER',
      detail: failed
        ? 'Existing INSTANCE binding conflicts with requested TYPE binding'
        : 'Applying bolt parameters sequentially in Revit.',
      status: failed ? 'error' : 'running',
      currentItem: failed ? 'MATERIAL' : 'BOLT_DIAMETER',
      processed: failed ? 3 : 1,
      total: failed ? 9 : 7,
      percent: failed ? 33 : 14,
      error: failed
        ? 'MATERIAL: Existing INSTANCE binding conflicts with requested TYPE binding'
        : undefined,
      items: failed ? [
        { id: 'connector/DESCRIPTION', label: 'DESCRIPTION', status: 'complete', sequence: 0 },
        { id: 'connector/DIAMETER', label: 'DIAMETER', status: 'complete', sequence: 1 },
        {
          id: 'connector/MATERIAL',
          label: 'MATERIAL',
          detail: 'Existing INSTANCE binding conflicts with requested TYPE binding',
          status: 'conflict',
          error: 'Existing INSTANCE binding conflicts with requested TYPE binding',
          sequence: 2,
        },
      ] : [
        { id: 'bolt/BOLT_DESCRIPTION', label: 'BOLT_DESCRIPTION', status: 'complete', sequence: 0 },
        {
          id: 'bolt/BOLT_DIAMETER',
          label: 'BOLT_DIAMETER',
          detail: failed ? 'Shared parameter definition was not found.' : 'Adding to Gasket families.',
          status: failed ? 'error' : 'running',
          error: failed ? 'Shared parameter definition was not found.' : undefined,
          sequence: 1,
        },
        { id: 'bolt/BOLT_LENGTH', label: 'BOLT_LENGTH', status: 'pending', sequence: 2 },
      ],
      steps: [
        { id: 'connector', label: 'Create connector parameters', status: failed ? 'error' : 'complete' },
        { id: 'quickFill', label: 'Update family values in Quick Fill', status: failed ? 'pending' : 'complete' },
        {
          id: 'bolt',
          label: 'Create bolt parameters',
          detail: 'Selected bolt-property setup; 7 setup parameters affected.',
          status: failed ? 'pending' : 'current',
        },
        {
          id: 'additional',
          label: 'Create additional-category parameters',
          detail: 'Additional-category setup; 2 setup parameters affected.',
          status: 'pending',
        },
        {
          id: 'processProject',
          label: 'Create process and project parameters',
          detail: 'Process and project setup; 25 setup parameters affected.',
          status: failed ? 'pending' : 'skipped',
        },
        { id: 'verify', label: 'Check project parameters', status: 'pending' },
      ],
      metrics: [
        { label: 'Missing parameters', value: '42', tone: 'warning' },
        {
          label: 'Areas',
          value: 'Connector, Bolt properties, Additional categories, Process and project',
          tone: 'info',
        },
      ],
      locked: !failed,
    },
    readinessStatus: failed ? 'blocked' : 'repairing',
    readinessMessage: failed
      ? 'MATERIAL: Existing INSTANCE binding conflicts with requested TYPE binding'
      : '42 mapped Revit parameters are missing',
    resolvedProfileName: 'Default 4',
  });

  if (!rawModel) {
    return null;
  }

  const staleVerifyModel: OperationProgressModel | null = failed
    ? {
        ...rawModel,
        message: 'Checking project parameters',
        detail: 'Revit is checking project parameters after setup changes.',
        status: 'running',
        percent: 92,
        items: [],
        steps: rawModel.steps?.map((step) => ({
          ...step,
          status: step.id === 'verifyProject' ? 'current' : 'complete',
        })),
      }
    : null;

  return stabilizeProfileApplyWindowModel(staleVerifyModel, rawModel);
}

const MappingConfigurationPageContent: React.FC<MappingConfigurationPageContentProps> = (props) => {
  const model = useMappingWorkspaceContentModel(props);
  const { defaultParametersPromptDialog } = useIncludeDefaultPipeexParametersPrompt({
    enabled: props.lastAppliedProfileKey !== '',
    suppressAndExclude: props.skipSetupReadiness ||
      (props.projectPreferredProfile?.profileKey === props.lastAppliedProfileKey &&
        props.projectPreferredProfile.setupReadinessPolicy === 'legacy-conversion-bypass'),
    onIncludeConfirmed: () => model.grantRepairConsent(undefined, { allowWithoutProjectTrust: true }),
  });
  const renameProfile = model.hostProfileRenameDialogProfileKey
    ? getProfileKeyParts(model.hostProfileRenameDialogProfileKey)
    : null;
  const exportProfileKey = model.selectedProfileKey || props.lastAppliedProfileKey;
  const exportProfile = exportProfileKey ? getProfileKeyParts(exportProfileKey) : undefined;
  const exportProfileSource = model.selectedProfileKey
    ? 'selected'
    : props.lastAppliedProfileKey
      ? 'applied-fallback'
      : 'export-active-fallback';
  const e2eProfileApplyProgressModel = React.useMemo(() => {
    const config = getE2eBootstrapConfig(window.location.search, process.env.NODE_ENV);
    return config?.mappingProfileProgress
      ? buildE2eProfileApplyProgressModel(config.mappingProfileProgress)
      : null;
  }, []);

  React.useEffect(() => {
    traceMappingFlow('export profile resolved', {
      selectedProfileTraceKey: toTraceKey(model.selectedProfileKey),
      appliedProfileTraceKey: toTraceKey(props.lastAppliedProfileKey),
      profileName: exportProfile?.name ?? null,
      settingsType: exportProfile?.settingsType ?? null,
      source: exportProfileSource,
    });
  }, [
    exportProfile?.name,
    exportProfile?.settingsType,
    exportProfileSource,
    model.selectedProfileKey,
    props.lastAppliedProfileKey,
  ]);

  const isQuickFillTab = model.activeTab === 1;
  const secondaryActionText = model.isSaving
    ? isQuickFillTab ? 'Applying…' : 'Applying mapping…'
    : isQuickFillTab ? 'Apply and stay' : 'Apply mapping and stay';

  const footerSummaryContent = model.activeTab === 0 ? (
    <WorkspaceFooter
      statusLabel={model.workspaceSummaryText}
      detailText="Apply changes without closing the workspace."
      requiredOpen={model.workspaceSummary.requiredOpen}
      compact={true}
    />
  ) : model.activeTab === 3 ? null : (
    <CaptionText sx={{ color: 'text.secondary' }}>
      Return to PCF mapping.
    </CaptionText>
  );

  return (
    <MainLayout
      title=""
      ariaLabel="PCF mapping workspace"
      windowVariant="workspace"
      compactWorkflowChrome={true}
      workflowChromeVariant="workspace"
      hideUtilityChrome={true}
      showRuntimeBadgeInUtilityChrome={false}
      showBack={model.activeTab === 0 && model.showMappingWorkspaceBackButton}
      showNext={model.activeTab !== 2 && model.activeTab !== 3}
      showSecondaryAction={model.activeTab !== 2 && model.activeTab !== 3}
      onBack={model.handleMappingBack}
      onSecondaryAction={model.handleWorkspaceApply}
      onNext={model.handleWorkspaceContinue}
      nextText={model.isSaving ? 'Saving mapping…' : 'Save mapping and close'}
      secondaryActionText={secondaryActionText}
      nextDisabled={model.workspaceContinueDisabled}
      secondaryActionDisabled={model.workspaceApplyDisabled}
      secondaryActionAttention={model.workspaceApplyNeedsAttention}
      onReinitiateWorkflow={model.activeTab === 3 ? undefined : props.onReinitiateWorkflow}
      workflowAppliedOverride={model.showCompletedWorkflowChrome}
      disableContentScroll={true}
      footerContent={footerSummaryContent}
      navigationAreaRef={model.focusRefs.footerActions}
      navigationActionsRef={model.focusRefs.footerActions}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          gap: 0,
        }}
      >
        {!!model.saveError && (
          <WorkspaceStatusBanner
            tone="error"
            onClose={() => model.setSaveError(null)}
            dataTestId="mapping-save-error-banner"
          >
            {model.saveError}
          </WorkspaceStatusBanner>
        )}
        {!!model.profileError && (
          <WorkspaceStatusBanner tone="warning" dataTestId="mapping-profile-error-banner">
            {model.profileError}
          </WorkspaceStatusBanner>
        )}
        {model.projectIdentityPending && (
          <WorkspaceStatusBanner tone="info" dataTestId="mapping-project-pending-banner">
            Open a Revit project to load its mapping profile and parameter sources.
          </WorkspaceStatusBanner>
        )}
        {model.showProjectProfileVerifiedBanner && (
          <WorkspaceCallout
            tone="success"
            title={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <ValidationIcon sx={{ fontSize: 14 }} />
                <span>Mapping profile verified for this Revit project: {model.resolvedProfileName}.</span>
              </Box>
            }
          />
        )}
        {model.repairConfirmationDialog}
        {model.profileLoadConfirmationDialog}
        {model.unsavedTabChangeDialog}
        {defaultParametersPromptDialog}
        <MappingReadinessAlert
          readiness={model.mappingReadiness}
          modelDataStale={model.modelDataStale}
          onRefreshModelData={model.handleRefreshModelData}
          onRepairSetup={model.handleRepairSetupConfirm}
          onOpenQuickFill={model.handleOpenQuickFill}
          onReinitiateWorkflow={props.onReinitiateWorkflow}
        />
        <MappingProjectProfileGateDialog
          open={model.projectProfileGateOpen}
          activeDocumentName={model.activeDocumentInfo.documentName}
          projectKey={model.activeDocumentInfo.projectKey}
          profiles={model.profiles}
          profilesLoading={model.profilesLoading}
          profileMatches={model.profileProjectMatches.matches}
          profileMatchesLoading={model.profileProjectMatches.isLoading}
          topMatchProfileKey={model.profileProjectMatches.topMatchProfileKey}
          lastLoadedProfileKey={model.projectPreferredProfile?.profileKey ?? null}
          catalogReady={model.profileProjectMatches.catalogReady}
          catalogSource={model.profileProjectMatches.catalogSource}
          catalogError={model.profileProjectMatches.catalogError}
          selectedSettingsType={model.selectedSettingsType}
          selectedProfileKey={model.projectProfileGateProfileKey}
          isSaving={model.isSaving}
          profileError={model.profileError}
          onSelectedProfileKeyChange={model.handleProjectProfileGateSelectedProfileKeyChange}
          onRefreshProfiles={model.onRefreshProfiles}
          onReinitiateWorkflow={props.onReinitiateWorkflow}
          onConfirm={() => {
            void model.handleProjectProfileGateConfirm();
          }}
        />
        <OperationProgressWindowRequest
          id="mapping-profile-apply-progress"
          open={Boolean(e2eProfileApplyProgressModel) || model.profileApplyWindowOpen}
          priority={120}
          maxWidth="md"
          model={e2eProfileApplyProgressModel ?? model.profileApplyWindowModel ?? {
             title: 'Applying PCF mapping profile',
             message: 'Preparing Revit parameter mappings and export settings',
            status: 'running',
            percent: null,
            locked: true,
          }}
          onClose={
            !e2eProfileApplyProgressModel && model.profileApplyWindowModel?.status === 'error'
              ? model.handleProfileApplyProgressClose
              : undefined
          }
        />
        <OperationProgressWindowRequest
          id="mapping-quick-fill-profile-restore-progress"
          open={model.quickFillProfileRestoreWindowOpen && !model.profileApplyWindowOpen}
          priority={80}
          model={model.quickFillProfileRestoreProgressModel}
        />

        <ProfileSaveAsDialog
          open={model.hostProfileSaveAsDialogOpen}
          settingsType={model.selectedSettingsType}
          isSaving={model.isSaving}
          onClose={() => model.setHostProfileSaveAsDialogOpen(false)}
          onSave={model.onSaveProfileAs}
        />

        <ProfileSaveAsDialog
          open={renameProfile !== null}
          settingsType={renameProfile?.settingsType ?? model.selectedSettingsType}
          isSaving={model.isSaving}
          mode="rename"
          initialProfileName={renameProfile?.name}
          onClose={() => model.setHostProfileRenameDialogProfileKey(null)}
          onSave={(profileName) => model.onRenameProfile(
            model.hostProfileRenameDialogProfileKey ?? '',
            profileName
          )}
        />

        <MappingSetupSummaryDialog
          open={model.showMappingWorkspaceSummary && model.summaryWindowOpen}
          onClose={model.handleCloseSummaryWindow}
          onOpenTour={model.handleOpenMappingTour}
          onReturnToSetup={!model.showCompletedWorkflowChrome ? model.handleReturnToGuidedSetup : undefined}
          chips={model.workflowSummaryChips}
          cards={model.workflowSummaryCards}
          guides={model.workspaceGuides}
          activeGuideId={model.activeTab}
          onSelectGuide={(guideId) =>
            model.handleSelectWorkspaceTab(toMappingWorkspaceTabIndex(guideId))
          }
        />
        <MappingWorkspaceGuideDialog
          open={model.activeTab === 0 && model.mappingGuideOpen}
          onClose={() => model.setMappingGuideOpen(false)}
          onStartTour={model.handleOpenMappingTour}
          selectedProfileName={model.selectedProfileName}
          scopeCount={model.contextObjectTypes.length}
          totalMappings={model.workspaceSummary.total}
          mappedMappings={model.workspaceSummary.mapped}
          requiredOpen={model.workspaceSummary.requiredOpen}
          workspaceReady={model.workspaceReady}
        />

        {model.activeValidationTargets.length > 0 && model.shouldShowWorkspaceValidationNotice(model.activeTab) && (
          <ValidationNavigationNotice applicableTargetPages={[...model.activeValidationTargets]} />
        )}

        <MappingTabPanel value={model.activeTab} index={0}>
          {model.workspaceBootLoading ? (
            <MappingWorkspaceLoadingPlaceholder model={model.workspaceLoadingModel} />
          ) : (
          <MappingWorkspaceCanvas
            isMobile={model.isMobile}
            mobileMenuOpen={model.mobileMenuOpen}
            onOpenMobileMenu={() => model.setMobileMenuOpen(true)}
            onCloseMobileMenu={() => model.setMobileMenuOpen(false)}
            focusRefs={model.focusRefs}
            objectTypes={model.contextObjectTypes}
            selectedNode={model.selectedNode}
            onNodeSelect={model.handleNodeSelect}
            onAddObjectType={model.handleAddObjectType}
            onDeleteObjectType={model.handleDeleteObjectType}
            isTreeCollapsed={model.isTreeCollapsed}
            onToggleTreeCollapsed={() => model.setIsTreeCollapsed(!model.isTreeCollapsed)}
            scopeSummaries={model.scopeSummaries}
            sectionSummaries={model.sectionSummaries}
            workspaceNavigatorSummary={model.workspaceNavigatorSummary}
            highlightedAttributeNames={model.highlightedAttributeNames}
            processConditionsEditorState={model.processConditionsEditorState}
            onProcessConditionsEditorStateChange={model.handleProcessConditionsEditorStateChange}
            projectPropertiesEditorState={model.projectPropertiesEditorState}
            onProjectPropertiesEditorStateChange={model.handleProjectPropertiesEditorStateChange}
          />
          )}
        </MappingTabPanel>

        <MappingTabPanel value={model.activeTab} index={1}>
          <Box
            ref={model.setQuickFillWorkspaceShellRef}
            data-testid="mapping-quickfill-tab-shell"
            sx={{
              flex: 1,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
            }}
          >
            {model.projectProfileGateBlocksActions || model.quickFillProfileRestorePending ? (
              null
            ) : (
              <QuickFillPageRedux
                onGoBack={model.handleMappingBack}
                onContinue={() => {
                  // Just apply changes, no navigation
                }}
                embedded={true}
                footerActionsRef={model.focusRefs.footerActions}
                registerEmbeddedActions={(actions) => {
                  model.focusRefs.quickFillActions.current = actions;
                }}
              />
            )}
          </Box>
        </MappingTabPanel>

        <MappingTabPanel value={model.activeTab} index={2}>
          <Box
            ref={model.focusRefs.workspaceCanvas}
            sx={{
              flex: 1,
              overflow: 'hidden',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <PcfValidationContent
              embedded={true}
              resumeScopeKey={model.resumeScopeKey}
              registerEmbeddedActions={(actions) => {
                model.focusRefs.validationGuideActions.current = actions;
              }}
            />
          </Box>
        </MappingTabPanel>

        <MappingTabPanel value={model.activeTab} index={3}>
          <Box
            ref={model.focusRefs.workspaceCanvas}
            sx={{
              flex: 1,
              overflow: 'hidden',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ExportPcfContent
              onStatusClick={model.handleOpenValidationForSystem}
              showGuideButton={false}
              exportActionsDisabled={model.exportActionsDisabled}
              exportActionsDisabledReason={model.exportActionsDisabledReason}
              profile={exportProfile}
              registerEmbeddedActions={(actions) => {
                model.focusRefs.exportGuideActions.current = actions;
              }}
            />
          </Box>
        </MappingTabPanel>

        <WorkspaceSpotlightTour
          open={model.activeTab === 0 && model.mappingTourOpen}
          steps={model.mappingTourSteps}
          stepIndex={model.mappingTourStep}
          onBack={() => model.setMappingTourStep((current) => Math.max(0, current - 1))}
          onNext={() =>
            model.setMappingTourStep((current) => Math.min(model.mappingTourSteps.length - 1, current + 1))
          }
          onJumpToStep={model.setMappingTourStep}
          onClose={model.handleCloseMappingTour}
          dialogLabel="PCF mapping workspace tour"
          showArrows
        />
      </Box>
    </MainLayout>
  );
};

export default MappingConfigurationPageContent;
